import React, { useState, useEffect } from 'react';
import {
  TextField,
  Divider,
  Alert,
  Autocomplete,
  Box,
  Typography,
} from '@mui/material';
import { CallReceivedOutlined } from '@mui/icons-material';
import OrderFormLayout from '../../components/orders/OrderFormLayout';
import InboundItemsTable, {
  InboundOrderItem,
  AvailableItem,
} from '../../components/orders/InboundItemsTable';
import { api } from '../../services/api';
import { Partner, OrderDirection } from '../../types/api';
import { useNotification } from '../../contexts/NotificationContext';

const InboundOrderCreate: React.FC = () => {
  const { alert } = useNotification();

  // Form state
  const [selectedCustomer, setSelectedCustomer] = useState<Partner | null>(null);
  const [returnReason, setReturnReason] = useState('');
  const [remark, setRemark] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Customers state
  const [customers, setCustomers] = useState<Partner[]>([]);

  // Items state
  const [availableItems, setAvailableItems] = useState<AvailableItem[]>([]);
  const [orderItems, setOrderItems] = useState<InboundOrderItem[]>([
    {
      item_id: 0,
      item_name: '',
      quantity: 0,
      note: '',
    },
  ]);

  // Fetch customers and items on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersRes, itemsRes] = await Promise.all([
          api.get('/api/partners', { params: { type: 'CUSTOMER' } }),
          api.get('/api/items'),
        ]);
        setCustomers(customersRes.data.partners || []);
        setAvailableItems((itemsRes.data.items || []).map((item: any) => ({
          ...item,
          units_per_carton: item.metadata?.units_per_carton ?? 0,
        })));
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      }
    };
    fetchData();
  }, []);

  const validateForm = (): boolean => {
    // Check if customer is selected
    if (!selectedCustomer) {
      setError('Please select a customer');
      return false;
    }

    // Check if at least one item with valid quantity
    const validItems = orderItems.filter(
      (item) => item.item_id > 0 && item.quantity > 0
    );

    if (validItems.length === 0) {
      setError('Please add at least one item with a valid quantity');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    setLoading(true);

    const payload = {
      direction: OrderDirection.RETURN,
      partner_id: selectedCustomer?.id,
      items: orderItems
        .filter((item) => item.item_id > 0)
        .map((item) => ({
          item_id: item.item_id,
          order_qty: item.quantity,
          note: item.note,
        })),
      metadata: {
        return_reason: returnReason,
        note: remark || 'Customer return',
      },
    };

    try {
      const response = await api.post('/api/orders/instant', payload);
      const orderId = response.data?.id || response.data?.order?.id;
      // Reset form
      setSelectedCustomer(null);
      setReturnReason('');
      setRemark('');
      setOrderItems([{ item_id: 0, item_name: '', quantity: 0, note: '' }]);
      // Show success dialog
      await alert({
        title: 'Order Created',
        message: `Return Order #${orderId || ''} created successfully!`,
        icon: 'success',
      });
    } catch (err: any) {
      setError(err.message|| 'Failed to create order');
      console.error('Error creating order:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OrderFormLayout
      title="Create Return / Inward Order"
      icon={<CallReceivedOutlined sx={{ fontSize: 28 }} />}
      onSubmit={handleSubmit}
      submitLabel="Create Return Order"
      loadingLabel="Creating..."
      loading={loading}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Customer Selection */}
      <Autocomplete
        fullWidth
        options={customers}
        getOptionLabel={(option) => option.name}
        value={selectedCustomer}
        onChange={(_, newValue) => setSelectedCustomer(newValue)}
        filterOptions={(options, { inputValue }) => {
          const filterValue = inputValue.toLowerCase();
          return options.filter((option) =>
            option.name.toLowerCase().includes(filterValue)
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Customer"
            placeholder="Type to search customer..."
            required
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box>
              <Typography variant="body1">{option.name}</Typography>
            </Box>
          </li>
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 2 }} />

      {/* Items Table */}
      <InboundItemsTable
        items={orderItems}
        availableItems={availableItems}
        onItemsChange={setOrderItems}
      />

      <Divider sx={{ my: 2 }} />

      {/* Remark */}
      <TextField
        fullWidth
        label="Return Notes"
        multiline
        rows={2}
        value={remark}
        onChange={(e) => setRemark(e.target.value)}
        placeholder="Add details about the return (condition, reason, etc.)"
      />
    </OrderFormLayout>
  );
};

export default InboundOrderCreate;
